import * as R from 'remeda';
import { Button, Stack } from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';
import { AddFilter } from './components/AddFilter';
import { FilterChip } from './components/FilterChip';
import { FilterSettings, FiltersObject } from './types';

type FiltersProps<O extends FiltersObject> = {
  dataCy: string;
  filterSettings: FilterSettings<O>[];
  filters: O;
  setFilters: (params: O | undefined) => void;
  isLoading?: boolean;
};

export const Filters = <O extends FiltersObject>({
  dataCy,
  filterSettings,
  filters,
  setFilters,
  isLoading,
}: FiltersProps<O>) => {
  const intl = useIntl();

  const areFiltersReady = !isLoading;

  const handleClearAllFilters = () => setFilters(undefined);

  const [appliedSettings, unusedSettings] = R.partition(
    filterSettings,
    setting => setting.valueGetter(filters) !== undefined
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      justifyContent="space-between"
      data-cy={dataCy}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {areFiltersReady ? (
          <>
            {appliedSettings.map(filterSetting => {
              const isValid = !!filterSetting.validateFilter?.(
                filterSetting.valueGetter(filters)
              );

              return (
                <FilterChip
                  key={filterSetting.filterKey}
                  filterSetting={filterSetting}
                  filters={filters}
                  isValid={isValid}
                />
              );
            })}
          </>
        ) : null}
        {unusedSettings.length > 0 ? (
          <AddFilter filterSettings={unusedSettings} />
        ) : null}
      </Stack>
      {areFiltersReady && !!appliedSettings.length && (
        <Button
          variant="text"
          color="secondary"
          sx={{ textWrap: 'nowrap' }}
          onClick={handleClearAllFilters}
          data-cy="clear-all-filters-button"
        >
          {intl.formatMessage({ id: 'containers.filtering.buttons.clear' })}
        </Button>
      )}
    </Stack>
  );
};
